import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { useLibraryCatalog, useCreateAssignment } from "@/lib/practiceQueries";

const LAYER_OPTIONS = [
  { value: "structure", label: "Structure" },
  { value: "vocal_delivery", label: "Vocal Delivery" },
  { value: "body_language", label: "Body Language" },
  { value: "slide_design", label: "Slide Design" },
  { value: "storytelling", label: "Storytelling" },
  { value: "qa_handling", label: "Q&A Handling" },
  { value: "executive_presence", label: "Executive Presence" },
];

// Renderers WITHOUT `supports_blocks` (see interaction_schema_contracts.
// RENDERER_REGISTRY) — the only ones create_assignment can create from
// just a Library item, with no isc_id/isv_id InteractionSchema to resolve.
const ASSIGNABLE_RENDERER_KEYS = new Set([
  "content_viewer", "upload_media", "rehearsal_recorder", "stage_map", "comparison_view", "timeline_journey",
]);

/**
 * Clients Restructure — Team 360's "+ Assign Practice" (Practice &
 * Layers) and "Add from Library" (Files & Videos) share this exact
 * modal — both create the SAME thing: a team-shared Assignment
 * (owner_member_id=null, see practice_repository.create_assignment),
 * never an individual one.
 */
export default function AssignFromLibraryModal({ workspaceId, onClose }) {
  const [layerKey, setLayerKey] = useState(LAYER_OPTIONS[0].value);
  const [selected, setSelected] = useState(null);
  const [title, setTitle] = useState("");
  const { data, isPending } = useLibraryCatalog(workspaceId);
  const createAssignment = useCreateAssignment(workspaceId);

  const items = useMemo(
    () => (data?.items || []).filter((it) => ASSIGNABLE_RENDERER_KEYS.has(it.renderer_key) && (it.primary_layer_key === layerKey || !it.primary_layer_key)),
    [data, layerKey],
  );

  function pick(item) {
    setSelected(item);
    setTitle(item.title);
  }

  async function handleSubmit() {
    if (!selected || !title.trim()) return;
    try {
      await createAssignment.mutateAsync({
        layer_key: layerKey, owner_member_id: null, libv_id: selected.libv_id, lib_id: selected.lib_id,
        renderer_key: selected.renderer_key, title_snapshot: title.trim(),
      });
      toast.success(`"${title.trim()}" is now shared with the team`);
      onClose();
    } catch (err) {
      toast.error(err.body?.detail || "Couldn't assign this to the team");
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg" data-testid="assign-from-library-modal">
        <DialogHeader>
          <DialogTitle className="font-serif">Add from Library</DialogTitle>
          <DialogDescription>Shares a Library resource with the whole team — never one member's private assignment.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="text-xs text-ink-soft uppercase mb-1.5 block">Layer</label>
            <Select value={layerKey} onValueChange={(v) => { setLayerKey(v); setSelected(null); }}>
              <SelectTrigger data-testid="assign-from-library-layer-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {LAYER_OPTIONS.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-xs text-ink-soft uppercase mb-1.5 block">Library item</label>
            {isPending ? (
              <p className="text-sm text-ink-soft">Loading catalog…</p>
            ) : items.length === 0 ? (
              <p className="text-sm text-ink-soft" data-testid="assign-from-library-empty">No directly-assignable published items for this layer yet.</p>
            ) : (
              <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1" data-testid="assign-from-library-item-list">
                {items.map((it) => (
                  <button
                    key={it.lib_id}
                    onClick={() => pick(it)}
                    className={`w-full text-left border rounded-lg px-3 py-2 text-sm transition-colors ${selected?.lib_id === it.lib_id ? "border-terracotta bg-terracotta/10 text-ink" : "border-ink/10 text-ink-soft hover:border-ink/20"}`}
                    data-testid={`assign-from-library-item-${it.lib_id}`}
                  >
                    <span className="font-medium text-ink">{it.title}</span>
                    {it.use_when && <span className="block text-xs text-ink-soft/80 mt-0.5">{it.use_when}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>

          {selected && (
            <div>
              <label className="text-xs text-ink-soft uppercase mb-1.5 block">Title shown to the team</label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="assign-from-library-title-input" />
            </div>
          )}
        </div>

        <DialogFooter>
          <button onClick={onClose} className="text-sm text-ink/60 px-4 py-2" data-testid="assign-from-library-cancel-btn">Cancel</button>
          <button
            onClick={handleSubmit}
            disabled={!selected || !title.trim() || createAssignment.isPending}
            className="bg-terracotta text-paper text-sm font-medium px-4 py-2 rounded-md hover:bg-terracotta/90 disabled:opacity-50"
            data-testid="assign-from-library-submit-btn"
          >
            {createAssignment.isPending ? "Sharing…" : "Share with team"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
